import axios from 'axios';
import type { Axios, AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders, AxiosError } from 'axios';
import { SSRContext } from 'vue/server-renderer';
import * as qs from 'qs';
import { getEnv } from '@/common/env';
import Cookie from '@/utils/cookie';
import LangManager from '@/locales/lang-manager';
import { NOTIFY_LANG, LANG_IDS } from '@/constants';
import ARMS from '@/common/arms';

const getTimezoneOffset = (): string => {
  const d: Date = new Date();
  const offset: number = d.getTimezoneOffset();
  const sign: string = (offset < 0) ? '+' : '-';
  const hour: number = parseInt(Math.abs((offset / 60)).toFixed(0));
  const minute: number = parseInt(Math.abs((offset % 60)).toFixed(0));
  return `GMT${sign}${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`;
};

export enum AjaxCode {
  OK = 0,
  NoLogin = -101,
  NotFound = -404,
  Unknown = -1,
};
export class AjaxError extends Error {
  code: AjaxCode | string;
  message: string;

  constructor(code: AjaxCode | string, message: string) {
    super();

    this.code = code;
    this.message = message;
  }
}
export class BaseServiceCommon {
  static context: SSRContext | undefined;

  static depends: BaseService[] = [];

  static setContext(context: SSRContext | undefined) {
    this.context = context;
    this.depends.forEach((dep: BaseService) => {
      dep.instance.defaults.headers = this.getHeaders();
      dep.instance.defaults.baseURL = this.getBaseURL();
    });
  };

  static getBaseURL() {
    const env = getEnv();
    const URL_MAP: Record<string, string> = {
      dev: '//dev.bilibili.tv:3000',
      uat: '//uat-api.bilibili.tv',
      pre: '//pre-api.bilibili.tv',
      prod: '//api.bilibili.tv',
    };
    let baseUrl = URL_MAP[env];
    return `${import.meta.env.SSR ? 'http:' : ''}${baseUrl}`;
  };

  static getHeaders(service?: BaseService) {
    if (import.meta.env.SSR && service) {
      this.depends.push(service);
    }
    if (!this.context) {
      return {};
    }
    const { host, accept, ...rest } = this.context?.headers;
    return { ...rest };
    // const { cookie, referer, 'user-agent': userAgent } = this.context?.headers;
    // return {
    //   cookie,
    //   referer,
    //   'user-agent': userAgent
    // };
  }

  static getParams() {
    const lang: string = (this.context ? this.context?.lang : LangManager.get()) || 'en';
    const langId: number = this.context ? LANG_IDS[lang] : LangManager.getID();
    const timezone: string = getTimezoneOffset();
    const csrf: any = Cookie.get('bili_jct', this.context?.headers);
    const platform: string = 'web';
    return {
      lang_id: langId,
      lang: NOTIFY_LANG[lang],
      s_locale: NOTIFY_LANG[lang],
      timezone,
      csrf,
      platform,
      ts: Date.now()
    };
  }

  static getTimeout(): number {
    const ssrTimeout = 500;
    const csrTimeout = 6000;
    return import.meta.env.SSR ? ssrTimeout : csrTimeout;
  }
}

export class BaseService {

  public instance: Axios;

  constructor() {
    this.instance = axios.create({
      baseURL: BaseServiceCommon.getBaseURL(),
      timeout: BaseServiceCommon.getTimeout(),
      headers: BaseServiceCommon.getHeaders(this),
      withCredentials: true,
    });

    this.useInterceptor();
  }

  private useInterceptor() {

    this.instance.interceptors.request.use((config: AxiosRequestConfig) => {
      config.params = config.responseType !== 'blob'
        ? { ...BaseServiceCommon.getParams(), ...config.params }
        : {};
      if (
        config.method === 'post'
        && (
          config.headers?.['Content-Type'] === 'application/x-www-form-urlencoded'
          || config.headers?.['content-Type'] === 'application/x-www-form-urlencoded'
        )
      ) {
        config.data = qs.stringify(config.data);
      }

      return config;
    }, error => {
      return Promise.reject(error);
    });

    this.instance.interceptors.response.use((response: AxiosResponse) => {
      const { config, status, statusText, data } = response;

      // arms api 请求成功上报
      const start = config.params?.ts || Date.now();
      const time = Date.now() - start;
      ARMS.logApi(config.url, true, time, status, statusText, start);

      if (response.config?.responseType === 'blob') {
        return response;
      }
      const { code, message } = data;

      if (code !== AjaxCode.OK) {
        return Promise.reject(new AjaxError(code, message));
      }
      return data;
    }, (error: AxiosError) => {
      const { config, status, code, response } = error;

      // arms api 请求失败上报
      const start = config.params?.ts || Date.now();
      const time = Date.now() - start;

      const errorCode = response?.status || code || AjaxCode.Unknown;
      const errorMessage = response?.statusText || status || error.message;

      ARMS.logApi(config.url, false, time, errorCode, errorMessage, start);
      console.log('**error(ajax): ', {
        url: config.url,
        time,
        errorCode,
        errorMessage,
        headers: config.headers,
        method: config.method,
        data: config.params || config.data
      });
      const ajaxError = new AjaxError(errorCode, errorMessage);
      return Promise.reject(ajaxError);
    });
  }

  public request(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.instance.request(config);
  }

  public get(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request({ method: 'get', ...config });
  }

  public post(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request({ method: 'post', ...config });
  }

  public postForm(config: AxiosRequestConfig): Promise<AxiosResponse> {
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    config.headers = { ...headers, ...config.headers };
    return this.post(config);
  }

  public postJson(config: AxiosRequestConfig): Promise<AxiosResponse> {
    const headers = { 'Content-Type': 'application/json' };
    config.headers = { ...headers, ...config.headers };
    return this.post(config);
  }
}

export default BaseService;
